"use strict";

import Common from "../../Common";
import CommonGame from "../../CommonGame";

export default class PalettePanel extends PIXI.Container {
    constructor() {
        super();

        // Constants.
        this.ITEMS_PER_ROW = 5;
        this.ITEMS_PER_PAGE = 15;
        this.PALETTE_TOOLS = Common.assets.getJSON("config").palettePanel.tools;
        this.PALETTE_TYPE_TO_TOOL = [
            this.PALETTE_TOOLS.pencil,
            this.PALETTE_TOOLS.brush,
            this.PALETTE_TOOLS.stamp,
            this.PALETTE_TOOLS.fill,
            this.PALETTE_TOOLS.ink,
            this.PALETTE_TOOLS.spray,
            this.PALETTE_TOOLS.fill
        ];

        // Attributes.
        this._bgGradientSprite = null;
        this._panel = null;
        this._boxSprite = null;
        this._type = null;
        this._nextBtn = null;
        this._prevBtn = null;
        this._pageIndex = 0;
        this._pageCount = 0;
        this._arrBtnInfo = [];

        // Signals.
        this.signals = {};
        this.signals.onColourChanged = new signals.Signal();
        this.signals.onStampChanged = new signals.Signal();
    }

    init() {
        // Bg gradient sprite.
        this._bgGradientSprite = new PIXI.Sprite(Common.blankTexture);
        this._bgGradientSprite.tint = 0x000000;
        this._bgGradientSprite.alpha = 0.6;
        this._bgGradientSprite.anchor = new PIXI.Point(0.5, 0.5);
        this._bgGradientSprite.scale.x = Common.STAGE_WIDTH * 1.25;
        this._bgGradientSprite.scale.y = Common.STAGE_HEIGHT;
        this._bgGradientSprite.interactive = true;
        this._bgGradientSprite.click = this._bgGradientSprite.touchend = this.hide.bind(this);
        this.addChild(this._bgGradientSprite);

        // Panel.
        this._panel = new PIXI.Container();
        this.addChild(this._panel);

        // Box sprite.
        this._boxSprite = new PIXI.Sprite(Common.assets.getTexture("colour_palette_big_bg"));
        this._boxSprite.interactive = true;
        this._boxSprite.anchor = new PIXI.Point(0.5, 0.5);
        this._panel.addChild(this._boxSprite);

        // Next button.
        var btnStatesAux = new P3.ButtonStates(
            Common.assets.getTexture("icon_next_off"),
            Common.assets.getTexture("icon_next_over"),
            Common.assets.getTexture("icon_next_down")
        );
        this._nextBtn = new P3.Button(btnStatesAux);
        this._nextBtn.anchor = new PIXI.Point(0.5, 0.5);
        this._nextBtn.scale = new PIXI.Point(1.5, 1.5);
        this._nextBtn.defaultScale = new PIXI.Point(1.5, 1.5);
        this._nextBtn.position.x = this._boxSprite.width * 0.5 + 50;
        this._nextBtn.alpha = 0;
        this._nextBtn.interactive = false;
        this._nextBtn.signals.click.add(this._onNextButton_Click, this);
        this._panel.addChild(this._nextBtn);

        // Prev button.
        btnStatesAux = new P3.ButtonStates(
            Common.assets.getTexture("icon_next_off"),
            Common.assets.getTexture("icon_next_over"),
            Common.assets.getTexture("icon_next_down")
        );
        this._prevBtn = new P3.Button(btnStatesAux);
        this._prevBtn.anchor = new PIXI.Point(0.5, 0.5);
        this._prevBtn.scale = new PIXI.Point(-1.5, 1.5);
        this._prevBtn.defaultScale = new PIXI.Point(-1.5, 1.5);
        this._prevBtn.position.x = -this._boxSprite.width * 0.5 - 50;
        this._prevBtn.alpha = 0;
        this._prevBtn.interactive = false;
        this._prevBtn.signals.click.add(this._onPrevButton_Click, this);
        this._panel.addChild(this._prevBtn);

        this.visible = false;
    }

    show(toolType) {
        this._type = toolType;

        this._arrBtnInfo = this._findItems(this._type);

        this._pageIndex = 0;
        this._updateNavControls();

        this._createItems(this._arrBtnInfo);

        // Animate-in.
        this.visible = true;
        this.alpha = 0;
        this._panel.y = -50;
        TweenMax.to(this._panel, 0.25, {y: 0, ease: Quad.easeOut});
        TweenMax.to(this, 0.25, {alpha: 1, ease: Quad.easeOut});

        // Sfx.
        Common.audio.playSound("sfx_palette_select_00");
    }

    changeType(toolType) {
        if (this._type === toolType) return;

        this._type = toolType;

        this._removeItems();
        this._pageIndex = 0;
        this._arrBtnInfo = this._findItems(this._type);

        this._updateNavControls();

        this._createItems(this._arrBtnInfo);
    }

    hide() {
        // Animate-out.
        TweenMax.to(this._panel, 0.25, {y: 50, ease: Quad.easeOut});
        TweenMax.to(this, 0.25, {
            alpha: 0,
            ease: Quad.easeOut,
            onComplete: function () {
                this.visible = false;

                this._boxSprite.removeChildren();

                // Tutorial
                Common.game.scene.addTutorialEvent(CommonGame.TUT_EVENT.PALETTE_PANEL_HIDDEN);
            },
            onCompleteScope: this
        });

        // Sfx.
        Common.audio.playSound("sfx_palette_close_00");
    }

    _removeItems() {
        for (var menuButton of this._boxSprite.children) {
            menuButton.interactive = false;
            TweenMax.to(menuButton.scale, 0.2, {
                x: 0,
                y: 0,
                ease: Sine.easeIn,
                delay: Math.random() * 0.2,
                onComplete: function (menuBtn) {
                    this._boxSprite.removeChild(menuBtn);
                },
                onCompleteScope: this,
                onCompleteParams: [menuButton]
            });
        }
    }

    _findItems(paletteType) {
        var result = [];

        var PALETTE_PAGES = Common.assets.getJSON("config").palettePanel.pages;
        var arrPageIndex = this.PALETTE_TYPE_TO_TOOL[paletteType];

        for (var pageIndex of arrPageIndex) {
            result.push([]);
            for (var item of PALETTE_PAGES[pageIndex]) {
                if (item.indexOf("#") == 0)
                // Colour.
                    result[result.length - 1].push({
                        out: "btns_colour_white",
                        over: "btns_colour_white",
                        down: "btns_colour_white",
                        tint: parseInt("0x" + item.substring(1)),
                        isShine: true
                    });
                else if (item.indexOf(CommonGame.TEXTURE_SPRITE_BASE_ID) != -1) {
                    // Texture.
                    var spriteId = CommonGame.TEXTURE_BUTTON_BASE_ID + item.substring(item.length - 2);
                    result[result.length - 1].push({out: spriteId, over: spriteId, down: spriteId, isShine: true});
                }
                else
                // Stamp.
                    result[result.length - 1].push({out: item, over: item, down: item, isShine: false});
            }
        }

        return result;
    }

    _createItems(arrBtnInfo) {
        // Create buttons for the selected page.
        var arrCurPageBtnInfo = this._arrBtnInfo[this._pageIndex];
        var btnIndex = 0;
        var colIndex = 0;
        var rowIndex = 0;
        var arcHeight = 7;
        var rowCount = Math.ceil(arrCurPageBtnInfo.length / this.ITEMS_PER_ROW);
        var startingX = -this._boxSprite.width * 0.5 + ( this._boxSprite.width - this._findItemCount(rowIndex, arrCurPageBtnInfo.length) * 135 + 125 ) * 0.5;
        var startingY = -this._boxSprite.height * 0.5 + ( this._boxSprite.height - ( rowCount - 1 ) * 130 ) * 0.5;
        // TODO: Set starting X and Y according to the number of items for the given row and the number of rows.
        for (var btnInfo of arrCurPageBtnInfo) {
            var btnStatesAux = new P3.ButtonStates(
                Common.assets.getTexture(btnInfo.out),
                Common.assets.getTexture(btnInfo.over),
                Common.assets.getTexture(btnInfo.down));
            var menuButton = new P3.Button(btnStatesAux);
            menuButton.tint = btnInfo.tint != null ? btnInfo.tint : 0xffffff;
            menuButton.anchor = new PIXI.Point(0.5, 0.5);
            menuButton.x = startingX + colIndex * 135;
            menuButton.y = startingY + rowIndex * 125;
            if (colIndex % 2 != 0) {
                menuButton.y += arcHeight;
                arcHeight *= -1;
            }
            menuButton.index = btnIndex;
            menuButton.signals.click.addOnce(this._onMenuButton_Click, this);
            this._boxSprite.addChild(menuButton);

            // Shine overlay.
            if (btnInfo.isShine) {
                var shineOverlay = new PIXI.Sprite(Common.assets.getTexture("btns_colour_overlay"));
                shineOverlay.anchor = new PIXI.Point(0.5, 0.5);
                shineOverlay.alpha = 0.4;
                shineOverlay.blendMode = PIXI.BLEND_MODES.ADD;
                menuButton.addChild(shineOverlay);
            }

            ++btnIndex;

            ++colIndex;
            if (colIndex >= this.ITEMS_PER_ROW) {
                colIndex = 0;
                ++rowIndex;

                startingX = -this._boxSprite.width * 0.5 + ( this._boxSprite.width - this._findItemCount(rowIndex, arrCurPageBtnInfo.length) * 135 + 125 ) * 0.5;
            }

            // Animate the button.
            menuButton.scale = new PIXI.Point();
            TweenMax.to(menuButton.scale, 0.2, {x: 1, y: 1, ease: Sine.easeOut, delay: Math.random() * 0.2});
        }
    }

    _findItemCount(rowIndex, btnCount) {
        let result = 0;

        let remainingBtnCount = btnCount - rowIndex * this.ITEMS_PER_ROW;
        if (remainingBtnCount / this.ITEMS_PER_ROW >= 1) result = this.ITEMS_PER_ROW;
        else result = remainingBtnCount % this.ITEMS_PER_ROW;

        return result;
    }

    _updateNavControls() {
        if (this._prevBtn.alpha === 0) {
            if (this._pageIndex > 0) {
                TweenMax.to(this._prevBtn, 0.25, {alpha: 1, ease: Sine.easeOut});
                this._prevBtn.interactive = true;
            }
        }
        else {
            if (this._pageIndex === 0) {
                TweenMax.to(this._prevBtn, 0.25, {alpha: 0, ease: Sine.easeIn});
                this._prevBtn.interactive = false;
            }
        }

        if (this._nextBtn.alpha === 0) {
            if (this._pageIndex < this._arrBtnInfo.length - 1) {
                TweenMax.to(this._nextBtn, 0.25, {alpha: 1, ease: Sine.easeOut});
                this._nextBtn.interactive = true;
            }
        }
        else {
            if (this._pageIndex === this._arrBtnInfo.length - 1) {
                TweenMax.to(this._nextBtn, 0.25, {alpha: 0, ease: Sine.easeIn});
                this._nextBtn.interactive = false;
            }
        }
    }

    //----EVENTS----//

    _onPageIndex_Changed() {
        // Navigation controls.
        this._updateNavControls();

        // Menu buttons.
        this._removeItems();
        this._createItems(this._arrBtnInfo);
    }

    _onNextButton_Click() {
        if (this._pageIndex < this._arrBtnInfo.length - 1) {
            this._pageIndex += 1;

            this._onPageIndex_Changed();
        }
    }

    _onPrevButton_Click() {
        if (this._pageIndex > 0) {
            this._pageIndex -= 1;

            this._onPageIndex_Changed();
        }
    }

    _onMenuButton_Click(btn) {
        switch (this._type) {
            case CommonGame.TOOL.PENCIL:
            case CommonGame.TOOL.BRUSH:
            case CommonGame.TOOL.BUCKET:
            case CommonGame.TOOL.INK_POT:
            case CommonGame.TOOL.SPRAY:
            {
                this.signals.onColourChanged.dispatch(
                    Common.assets.getJSON("config").palettePanel.pages[this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex]][btn.index],
                    this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex],
                    btn.index);

                break;
            }

            case CommonGame.TOOL.STAMP:
            {
                this.signals.onStampChanged.dispatch(
                    Common.assets.getJSON("config").palettePanel.pages[this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex]][btn.index],
                    this.PALETTE_TYPE_TO_TOOL[this._type][this._pageIndex],
                    btn.index);

                break;
            }
        }

        this.hide();

        // Sfx.
        Common.audio.playSound("sfx_generic_colour_type_change_00");
    }

}
